import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlarmClock,
  Flame,
  ArrowUpDown,
  Check,
  ChevronDown,
  Filter,
  FilterX,
  ListChecks,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageMotion,
  Scroller,
  Select,
  confirmDialog,
  promptDialog,
} from '@/shared/ui';
import { TicketList } from '@/widgets/TicketList';
import { ToolbarSearch } from '@/widgets/ToolbarSearch';
import { useAuthStore } from '@/features/auth';
import { useTicketsStore } from '@/features/tickets';
import { useMassIncidentUi } from '@/features/mass-incidents';
import {
  apiErrorText,
  categoriesApi,
  massIncidentsApi,
  supportGroupsApi,
  ticketViewsApi,
  type SupportGroup,
  type TicketListView,
} from '@/shared/api';
import type { Category } from '@/entities/category';
import { pluralRu } from '@/shared/lib/plural';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { useUrlParam } from '@/shared/lib/useUrlParam';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { useFocusTrap } from '@/shared/lib/useFocusTrap';
import { cn } from '@/shared/lib/cn';
import type { TicketsListParams } from '@/shared/api/tickets';
import {
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  AssigneeIcon,
  BoardViewIcon,
  CategoryIcon,
  ClientIcon,
  GroupIcon,
  MassIncidentIcon,
  ServiceIcon,
  SlaIcon,
  StatusIcon,
  TaskPriorityIcon,
  ticketStatusLabel,
} from '@/shared/config';
import type { LucideIcon } from 'lucide-react';
import { ActiveFiltersBar, MobileSetsRow, SetTabs, type ActiveFilterChip } from './FilterSetsBar';
import {
  STATUS_OPEN,
  clearPatch,
  matchesPreset,
  withoutTab,
  revealSelected,
  snapshotOf,
  presetPatch,
} from './filter-sets';
import styles from './TicketsList.module.css';

const CHIP_STATUSES = TICKET_STATUSES;

type StaffTab = 'all' | 'group';
type Who = '' | 'unassigned' | 'involved' | 'me';

const WHO_OPTIONS: { value: Exclude<Who, ''>; label: string }[] = [
  { value: 'unassigned', label: 'Без исполнителя' },
  { value: 'involved', label: 'Мои' },
  { value: 'me', label: 'Я исполнитель' },
];

const STATUS_ALL = 'all';

type SortKey = NonNullable<TicketsListParams['sort']>;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'createdAt', label: 'По дате создания' },
  { value: 'updatedAt', label: 'По дате обновления' },
  { value: 'statusChangedAt', label: 'По смене статуса' },
  { value: 'priority', label: 'По приоритету' },
  { value: 'slaDue', label: 'По сроку SLA' },
];

const VIEW_TABS: { value: StaffTab; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'group', label: 'Группы' },
];

const SHEET_CLOSE_RATIO = 0.3;
const SHEET_CLOSE_VELOCITY = 0.5;

function useSheetDrag(sheetRef: React.RefObject<HTMLDivElement | null>, onClose: () => void) {
  const drag = useRef<{ id: number; startY: number; y: number; t: number; v: number } | null>(null);

  const move = useCallback(
    (el: HTMLDivElement, clientY: number) => {
      const d = drag.current;
      if (!d) return;
      const now = performance.now();
      const dt = now - d.t;
      if (dt > 0) d.v = (clientY - d.y) / dt;
      d.y = clientY;
      d.t = now;
      el.style.transform = `translateY(${Math.max(0, clientY - d.startY)}px)`;
    },
    [],
  );

  const end = useCallback(() => {
    const d = drag.current;
    const el = sheetRef.current;
    drag.current = null;
    if (!el) return;
    el.classList.remove(styles.sheetDragging);
    if (!d) {
      el.style.transform = '';
      return;
    }
    const passed = d.y - d.startY;
    if (passed > el.offsetHeight * SHEET_CLOSE_RATIO || d.v > SHEET_CLOSE_VELOCITY) {
      onClose();
      return;
    }
    el.style.transform = '';
  }, [onClose, sheetRef]);

  return useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
        const el = sheetRef.current;
        if (!el || drag.current) return;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
        }
        el.classList.add(styles.sheetDragging);
        drag.current = { id: e.pointerId, startY: e.clientY, y: e.clientY, t: performance.now(), v: 0 };
      },
      onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
        const el = sheetRef.current;
        if (!el || drag.current?.id !== e.pointerId) return;
        move(el, e.clientY);
      },
      onPointerUp: end,
      onPointerCancel: end,
    }),
    [end, move, sheetRef],
  );
}

const FilterGroup: React.FC<{ title: string; Icon: LucideIcon; children: React.ReactNode }> = ({
  title,
  Icon,
  children,
}) => (
  <div className={styles.group}>
    <div className={styles.groupLabel}>
      <Icon size={14} aria-hidden className={styles.groupIcon} />
      {title}
    </div>
    {children}
  </div>
);

function namesSummary(names: string[]): string {
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} и ещё ${names.length - 1}`;
}

const csv = (v: string) => v.split(',').filter(Boolean);

const WORKSPACE_SEARCH_KEY = 'workspace:search';

function readWorkspaceSearch(): string {
  try {
    return sessionStorage.getItem(WORKSPACE_SEARCH_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeWorkspaceSearch(search: string) {
  try {
    if (search) sessionStorage.setItem(WORKSPACE_SEARCH_KEY, search);
    else sessionStorage.removeItem(WORKSPACE_SEARCH_KEY);
  } catch {
  }
}

const SortMenu: React.FC<{
  value: SortKey;
  options: { value: SortKey; label: string }[];
  onChange: (v: SortKey) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value)?.label ?? '';
  const isDefault = value === options[0]?.value;

  return (
    <div className={styles.sortAnchor} ref={ref}>
      <IconButton
        label="Сортировка"
        tooltip={`Сортировка: ${current.toLowerCase()}`}
        size="lg"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn((!isDefault || open) && styles.actionOn)}
        onClick={() => setOpen((v) => !v)}
      >
        <ArrowUpDown size={18} aria-hidden />
      </IconButton>

      {open && (
        <div className={styles.sortPopover} role="listbox" aria-label="Сортировка">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                type="button"
                key={o.value}
                role="option"
                aria-selected={active}
                className={styles.sortOption}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                { }
                <span className={styles.sortOptionCheck} aria-hidden>
                  {active && <Check size={14} />}
                </span>
                <span className={styles.sortOptionLabel}>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const TicketsListPage: React.FC = () => {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isStaff = hasPermission('view_tickets');
  const canSeeGroups = hasPermission('view_internal_groups');

  const stats = useTicketsStore((s) => s.stats);
  const fetchStats = useTicketsStore((s) => s.fetchStats);

  const [rawTab] = useUrlParam<string>('tab', 'all');
  const tab: StaffTab = rawTab === 'group' ? 'group' : 'all';
  const [who, setWho] = useUrlParam<Who>('who', '');
  const [status, setStatus] = useUrlParam<string>('status', '');
  const [priority, setPriority] = useUrlParam<string>('priority', '');
  const [groupId, setGroupId] = useUrlParam<string>('groupId', '');
  const groupIds = useMemo(() => groupId.split(',').filter(Boolean), [groupId]);
  const [massIncident, setMassIncident] = useUrlParam<string>('mass', '');
  const oneIncidentId = massIncident && massIncident !== 'only' && massIncident !== 'hide' ? massIncident : '';
  const [oneIncidentTitle, setOneIncidentTitle] = useState('');
  useEffect(() => {
    setOneIncidentTitle('');
    if (!oneIncidentId) return;
    let cancelled = false;
    massIncidentsApi
      .get(oneIncidentId)
      .then((res) => !cancelled && setOneIncidentTitle(res.data.title))
      .catch(() => !cancelled && setOneIncidentTitle('Сбой не найден'));
    return () => {
      cancelled = true;
    };
  }, [oneIncidentId]);
  const canGroupIncidents = isStaff && hasPermission('change_status');
  const openIncidentPicker = useMassIncidentUi((s) => s.openPicker);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const togglePicked = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const stopSelecting = useCallback(() => {
    setSelecting(false);
    setPicked(new Set());
  }, []);
  const [slaParam, setSlaParam] = useUrlParam<'burning' | 'breached' | '1' | ''>('sla', '');
  const slaLevel: 'burning' | 'breached' | '' = slaParam === '1' ? 'breached' : slaParam;
  const [myClients, setMyClients] = useUrlParam<'1' | ''>('myclients', '');
  const [sort, setSort] = useUrlParam<SortKey>('sort', 'createdAt');
  const [cat, setCat] = useUrlParam<string>('cat', '');
  const [svc, setSvc] = useUrlParam<string>('svc', '');
  const catIds = useMemo(() => csv(cat), [cat]);
  const svcIds = useMemo(() => csv(svc), [svc]);
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [myGroups, setMyGroups] = useState<SupportGroup[]>([]);
  const [myGroupsLoaded, setMyGroupsLoaded] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [views, setViews] = useState<TicketListView[]>([]);

  const primaryGroupId = myGroups.find((g) => g.isPrimary)?.id ?? '';
  const groupTabDefault = myGroups.length > 1 && primaryGroupId ? primaryGroupId : 'my';
  const groupTabValue = groupId || (myGroupsLoaded ? groupTabDefault : 'my');
  const groupTabIds = useMemo(
    () => (groupTabValue === 'my' ? myGroups.map((g) => g.id) : csv(groupTabValue)),
    [groupTabValue, myGroups],
  );

  const [qUrl, setQUrl] = useUrlParam<string>('q', '');
  const [search, setSearch] = useState(qUrl);
  const q = useDebouncedValue(search.trim(), 350);
  const lastPushedQRef = useRef(qUrl);
  useEffect(() => {
    if (qUrl !== lastPushedQRef.current) {
      lastPushedQRef.current = qUrl;
      setSearch(qUrl);
    }
  }, [qUrl]);
  useEffect(() => {
    if (q !== search.trim()) return;
    if (q === qUrl) return;
    lastPushedQRef.current = q;
    setQUrl(q);
  }, [q, search, qUrl, setQUrl]);

  const [searchOpen, setSearchOpen] = useState(false);
  const searchPinned = useMediaQuery('(max-width: 768px)');

  const isMobile = useMediaQuery('(max-width: 640px)');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeSheet = useCallback(() => setFiltersOpen(false), []);
  const gripHandlers = useSheetDrag(sheetRef, closeSheet);
  const filtersRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  useFocusTrap(popoverRef, filtersOpen && !isMobile);
  useEffect(() => {
    if (!filtersOpen || isMobile) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('[data-side]')) return;
      if (filtersRef.current && !filtersRef.current.contains(target as Node)) setFiltersOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFiltersOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen, isMobile]);

  useEffect(() => {
    if (isStaff) void fetchStats();
  }, [isStaff, fetchStats]);

  useEffect(() => {
    if (!isStaff) return;
    supportGroupsApi
      .listMine()
      .then((res) => setMyGroups(res.data ?? []))
      .catch(() => setMyGroups([]))
      .finally(() => setMyGroupsLoaded(true));
    categoriesApi
      .list()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => setCategories([]));
    ticketViewsApi
      .list()
      .then(setViews)
      .catch(() => setViews([]));
  }, [isStaff]);

  useEffect(() => {
    if (!canSeeGroups) return;
    supportGroupsApi
      .listBrief()
      .then((res) => setGroups(res.data ?? []))
      .catch(() => setGroups([]));
  }, [canSeeGroups]);

  const statusChips = useMemo(() => {
    const byStatus = stats?.byStatus ?? {};
    const hasStats = Boolean(stats);
    const base = CHIP_STATUSES.map((s) => ({
      value: s as string,
      label: ticketStatusLabel(s),
      count: byStatus[s] ?? 0,
      showCount: hasStats,
    }));
    const known = new Set<string>(TICKET_STATUSES);
    const customLabels = stats?.statusLabels ?? {};
    const custom = Object.keys(byStatus)
      .filter((s) => !known.has(s))
      .map((s) => ({
        value: s,
        label: ticketStatusLabel(s, customLabels[s]),
        count: byStatus[s] ?? 0,
        showCount: true,
      }));
    return [...base, ...custom];
  }, [stats]);

  const statusOpen = status === STATUS_OPEN;
  const statusFilter = status === STATUS_ALL || statusOpen ? '' : status;
  const selectedStatuses = useMemo(
    () => new Set(statusFilter.split(',').filter(Boolean)),
    [statusFilter],
  );

  const toggleStatus = (v: string) => {
    setStatus((prev) => {
      const cur = prev === STATUS_ALL || prev === STATUS_OPEN ? [] : csv(prev);
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return next.join(',');
    });
  };


  const params = useMemo<TicketsListParams>(() => {
    const tabParams: TicketsListParams = !isStaff
      ? {}
      : tab === 'group'
        ? { view: 'pool', groupId: groupTabValue }
        : { view: 'pool' };
    return {
      ...tabParams,
      ...(q ? { q } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(isStaff && !statusFilter && !statusOpen ? { includeClosed: true } : {}),
      ...(isStaff && who ? { assignee: who } : {}),
      ...(priority ? { priority } : {}),
      ...(groupId && tab !== 'group' ? { groupId } : {}),
      ...(catIds.length ? { categoryIds: catIds } : {}),
      ...(svcIds.length ? { subcategoryIds: svcIds } : {}),
      ...(massIncident ? { massIncident } : {}),
      ...(slaLevel ? { slaLevel } : {}),
      ...(myClients ? { myClients: true } : {}),
      sort,
      dir: sort === 'slaDue' ? 'asc' : 'desc',
    };
  }, [isStaff, tab, groupTabValue, q, statusFilter, statusOpen, who, priority, groupId, catIds, svcIds, massIncident, slaLevel, myClients, sort]);

  const [searchParams, setSearchParams] = useSearchParams();

  const searchKey = searchParams.toString();
  const [restoreSearch] = useState(() => (searchKey ? '' : readWorkspaceSearch()));
  useEffect(() => {
    if (restoreSearch) setSearchParams(new URLSearchParams(restoreSearch), { replace: true });
  }, [restoreSearch, setSearchParams]);
  const restoringRef = useRef(Boolean(restoreSearch));
  if (restoringRef.current && searchKey) restoringRef.current = false;
  const restoring = restoringRef.current;
  useEffect(() => {
    if (!restoring) writeWorkspaceSearch(searchKey);
  }, [restoring, searchKey]);

  const patchUrlParams = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) p.set(k, v);
            else p.delete(k);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => setPicked(new Set()), [params]);
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) stopSelecting();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecting, stopSelecting]);

  useEffect(() => {
    if (rawTab !== 'new' && rawTab !== 'mine') return;
    patchUrlParams({
      tab: '',
      who: rawTab === 'new' ? 'unassigned' : 'involved',
      ...(status ? {} : { status: STATUS_OPEN }),
    });
  }, [rawTab, status, patchUrlParams]);

  const resetFilters = () => {
    patchUrlParams({
      who: '',
      status: '',
      priority: '',
      groupId: '',
      mass: '',
      sla: '',
      myclients: '',
      cat: '',
      svc: '',
    });
  };

  const handleTab = (next: StaffTab) => {
    const crossesGroupTab = (next === 'group') !== (tab === 'group');
    patchUrlParams({
      tab: next === 'all' ? '' : next,
      ...(crossesGroupTab ? { groupId: '' } : {}),
    });
  };

  const toggleGroupTabGroup = (id: string) => {
    const next = groupTabIds.includes(id) ? groupTabIds.filter((g) => g !== id) : [...groupTabIds, id];
    if (next.length === 0) return;
    setGroupId(next.length === myGroups.length ? 'my' : next.join(','));
  };

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of [...groups, ...myGroups]) m.set(g.id, g.name);
    return m;
  }, [groups, myGroups]);

  const groupTabLabel =
    tab !== 'group' || myGroups.length < 2
      ? 'Группы'
      : groupTabValue === 'my' || groupTabIds.length === myGroups.length
        ? 'Все мои группы'
        : namesSummary(groupTabIds.map((id) => groupNameById.get(id) ?? 'Группа'));

  const categoryOptions = useMemo(
    () => [
      { value: 'none', label: 'Без категории' },
      ...categories.map((c) => ({ value: c.id, label: c.name })),
    ],
    [categories],
  );
  const serviceOptions = useMemo(() => {
    const picked = new Set(catIds.filter((c) => c !== 'none'));
    const source = picked.size ? categories.filter((c) => picked.has(c.id)) : categories;
    return [
      { value: 'none', label: 'Без услуги' },
      ...source.flatMap((c) =>
        (c.Subcategories ?? []).map((sc) => ({ value: sc.id, label: sc.name, group: c.name })),
      ),
    ];
  }, [categories, catIds]);
  const labelOf = (options: { value: string; label: string }[], ids: string[]) =>
    ids.map((id) => options.find((o) => o.value === id)?.label ?? '…');

  const activeChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (who) {
      chips.push({
        key: 'who',
        label: WHO_OPTIONS.find((o) => o.value === who)?.label ?? who,
        Icon: AssigneeIcon,
        clear: () => setWho(''),
      });
    }
    if (statusOpen) {
      chips.push({ key: 'status', label: 'Открытые', Icon: StatusIcon, clear: () => setStatus('') });
    }
    if (statusFilter) {
      chips.push({
        key: 'status',
        label: namesSummary(csv(statusFilter).map((v) => statusChips.find((c) => c.value === v)?.label ?? v)),
        Icon: StatusIcon,
        clear: () => setStatus(''),
      });
    }
    if (slaLevel) {
      chips.push({
        key: 'sla',
        label: slaLevel === 'burning' ? 'Горят' : 'Просроченные',
        Icon: slaLevel === 'burning' ? Flame : AlarmClock,
        clear: () => setSlaParam(''),
      });
    }
    if (myClients) chips.push({ key: 'myclients', label: 'Мои клиенты', Icon: ClientIcon, clear: () => setMyClients('') });
    if (massIncident) {
      chips.push({
        key: 'mass',
        label:
          massIncident === 'only' ? 'Только сбои' : massIncident === 'hide' ? 'Без сбоев' : oneIncidentTitle || 'Один сбой',
        Icon: MassIncidentIcon,
        clear: () => setMassIncident(''),
      });
    }
    if (priority) {
      chips.push({
        key: 'priority',
        label: TICKET_PRIORITY_LABELS[priority as keyof typeof TICKET_PRIORITY_LABELS] ?? priority,
        Icon: TaskPriorityIcon,
        clear: () => setPriority(''),
      });
    }
    if (groupId) {
      const ids = tab === 'group' ? groupTabIds : groupIds;
      chips.push({
        key: 'group',
        label:
          groupId === 'my'
            ? 'Все мои группы'
            : namesSummary(ids.map((id) => (id === 'none' ? 'Без группы' : groupNameById.get(id) ?? 'Группа'))),
        Icon: GroupIcon,
        clear: () => setGroupId(''),
      });
    }
    if (catIds.length) {
      chips.push({ key: 'cat', label: namesSummary(labelOf(categoryOptions, catIds)), Icon: CategoryIcon, clear: () => setCat('') });
    }
    if (svcIds.length) {
      chips.push({ key: 'svc', label: namesSummary(labelOf(serviceOptions, svcIds)), Icon: ServiceIcon, clear: () => setSvc('') });
    }
    return chips;
  }, [who, setWho, statusOpen, statusFilter, statusChips, slaLevel, myClients, massIncident, oneIncidentTitle, priority, groupId, tab, groupTabIds, groupIds, groupNameById, catIds, svcIds, categoryOptions, serviceOptions, setStatus, setSlaParam, setMyClients, setMassIncident, setPriority, setGroupId, setCat, setSvc]);

  const activeFilterCount = activeChips.length;
  const hasFilters = activeFilterCount > 0;

  const snapshot = useMemo(() => snapshotOf(searchParams), [searchParams]);
  const shownViews = useMemo(() => views.filter((v) => !v.hidden), [views]);
  const hiddenPresets = useMemo(() => views.filter((v) => v.hidden), [views]);
  const appliedView =
    shownViews.find((v) => !v.preset && matchesPreset(v.query ?? {}, snapshot)) ??
    shownViews.find((v) => v.preset && matchesPreset(v.query ?? {}, snapshot)) ??
    null;
  const anySetApplied = Boolean(appliedView);
  const tabsViewportRef = useRef<HTMLDivElement>(null);
  const revealedRef = useRef(false);
  useLayoutEffect(() => {
    if (revealedRef.current || shownViews.length === 0) return;
    revealedRef.current = true;
    revealSelected(tabsViewportRef.current);
  }, [shownViews.length]);
  const canSaveSet = Object.keys(snapshot).some((k) => k !== 'tab') && !anySetApplied;

  const applyView = useCallback(
    (v: TicketListView) => {
      const q0 = v.query ?? {};
      if (appliedView?.id === v.id) patchUrlParams(clearPatch());
      else patchUrlParams(presetPatch(q0));
      setFiltersOpen(false);
    },
    [patchUrlParams, appliedView],
  );

  const saveView = useCallback(async () => {
    const name = await promptDialog({
      title: 'Сохранить набор фильтров',
      description: 'Вкладка, фильтры, сортировка и поиск вернутся одним щелчком. Набор видите только вы.',
      label: 'Название',
      placeholder: 'Горящие моей группы',
      confirmLabel: 'Сохранить',
    });
    if (!name?.trim()) return;
    try {
      const created = await ticketViewsApi.create({ name: name.trim(), query: withoutTab(snapshot) });
      setViews((prev) => [...prev.filter((v) => v.id !== created.id), created]);
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось сохранить набор'));
    }
  }, [snapshot]);

  const overwriteView = useCallback(
    async (v: TicketListView) => {
      try {
        const updated = await ticketViewsApi.update(v.id, { query: withoutTab(snapshot) });
        setViews((prev) => prev.map((x) => (x.id === v.id ? updated : x)));
        toast.success(`Набор «${v.name}» обновлён`);
      } catch (err) {
        toast.error(apiErrorText(err, 'Не удалось обновить набор'));
      }
    },
    [snapshot],
  );

  const renameView = useCallback(async (v: TicketListView) => {
    const name = await promptDialog({
      title: 'Переименовать набор',
      label: 'Название',
      defaultValue: v.name,
      confirmLabel: 'Сохранить',
    });
    if (!name?.trim() || name.trim() === v.name) return;
    try {
      const updated = await ticketViewsApi.update(v.id, { name: name.trim() });
      setViews((prev) => prev.map((x) => (x.id === v.id ? updated : x)));
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось переименовать набор'));
    }
  }, []);

  const removeView = useCallback(async (v: TicketListView) => {
    const ok = await confirmDialog({
      title: `Удалить набор «${v.name}»?`,
      description: v.preset
        ? 'Это встроенный набор — вернуть его можно в фильтрах, кнопкой «Вернуть встроенные».'
        : 'Фильтры на экране останутся как есть — исчезнет только закладка на них.',
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    try {
      await ticketViewsApi.remove(v.id);
      setViews((prev) =>
        v.preset ? prev.map((x) => (x.id === v.id ? { ...x, hidden: true } : x)) : prev.filter((x) => x.id !== v.id),
      );
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось удалить набор'));
    }
  }, []);

  const restorePresets = useCallback(async () => {
    try {
      const back = await Promise.all(hiddenPresets.map((v) => ticketViewsApi.update(v.id, { hidden: false })));
      const byId = new Map(back.map((v) => [v.id, v]));
      setViews((prev) => prev.map((x) => byId.get(x.id) ?? x));
      toast.success(back.length === 1 ? `Набор «${back[0].name}» вернулся` : 'Встроенные наборы вернулись');
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось вернуть наборы'));
    }
  }, [hiddenPresets]);

  const setProps = {
    views: shownViews,
    appliedId: appliedView?.id ?? null,
    canSave: canSaveSet,
    onApply: applyView,
    onSave: () => void saveView(),
    onOverwrite: (v: TicketListView) => void overwriteView(v),
    onRename: (v: TicketListView) => void renameView(v),
    onRemove: (v: TicketListView) => void removeView(v),
  };

  const filtersBody = (
    <div className={styles.filtersBody}>
      <div className={styles.filtersCol}>
      {isStaff && (
        <FilterGroup title="Исполнитель" Icon={AssigneeIcon}>
          <div className={styles.chipRow}>
            <button
              type="button"
              className={cn(styles.chip, !who && styles.chipActive)}
              aria-pressed={!who}
              onClick={() => setWho('')}
            >
              Любой
            </button>
            {WHO_OPTIONS.map((o) => (
              <button
                type="button"
                key={o.value}
                className={cn(styles.chip, who === o.value && styles.chipActive)}
                aria-pressed={who === o.value}
                onClick={() => setWho(who === o.value ? '' : o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </FilterGroup>
      )}

      <FilterGroup title="Статус" Icon={StatusIcon}>
        <div className={styles.chipRow}>
          { }
          <button
            type="button"
            className={cn(styles.chip, selectedStatuses.size === 0 && !statusOpen && styles.chipActive)}
            aria-pressed={selectedStatuses.size === 0 && !statusOpen}
            onClick={() => setStatus('')}
          >
            <span>Все</span>
            {stats && tab === 'all' && (
              <span
                className={cn(
                  styles.chipCount,
                  selectedStatuses.size === 0 && !statusOpen && styles.chipCountActive,
                )}
              >
                {stats.total}
              </span>
            )}
          </button>
          <button
            type="button"
            className={cn(styles.chip, statusOpen && styles.chipActive)}
            aria-pressed={statusOpen}
            title="Все, кроме закрытых, отозванных и отклонённых"
            onClick={() => setStatus(statusOpen ? '' : STATUS_OPEN)}
          >
            <span>Открытые</span>
          </button>
          {statusChips.map((chip) => {
            const active = selectedStatuses.has(chip.value);
            return (
              <button
                type="button"
                key={chip.value}
                className={cn(styles.chip, active && styles.chipActive)}
                aria-pressed={active}
                onClick={() => toggleStatus(chip.value)}
              >
                <span>{chip.label}</span>
                {chip.showCount && tab === 'all' && (
                  <span className={cn(styles.chipCount, active && styles.chipCountActive)}>
                    {chip.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {isStaff && (
        <FilterGroup title="SLA и клиенты" Icon={SlaIcon}>
          <div className={styles.chipRow}>
            { }
            <button
              type="button"
              className={cn(styles.chip, slaLevel === 'burning' && styles.chipActive)}
              aria-pressed={slaLevel === 'burning'}
              onClick={() => setSlaParam((v) => (v === 'burning' ? '' : 'burning'))}
            >
              <Flame size={14} aria-hidden />
              Горят
              {stats && tab === 'all' && (
                <span
                  className={cn(styles.chipCount, slaLevel === 'burning' && styles.chipCountActive)}
                >
                  {stats.slaBurning}
                </span>
              )}
            </button>
            <button
              type="button"
              className={cn(styles.chip, slaLevel === 'breached' && styles.chipActive)}
              aria-pressed={slaLevel === 'breached'}
              onClick={() => setSlaParam((v) => (v === 'breached' || v === '1' ? '' : 'breached'))}
            >
              <AlarmClock size={14} aria-hidden />
              Просроченные
              {stats && tab === 'all' && (
                <span
                  className={cn(styles.chipCount, slaLevel === 'breached' && styles.chipCountActive)}
                >
                  {stats.slaBreached}
                </span>
              )}
            </button>
            { }
            <button
              type="button"
              className={cn(styles.chip, myClients === '1' && styles.chipActive)}
              aria-pressed={myClients === '1'}
              onClick={() => setMyClients((v) => (v === '1' ? '' : '1'))}
            >
              <ClientIcon size={14} aria-hidden />
              Мои клиенты
            </button>
          </div>
        </FilterGroup>
      )}

      {isStaff && (
        <FilterGroup title="Массовые сбои" Icon={MassIncidentIcon}>
          <div className={styles.chipRow}>
            {oneIncidentId && (
              <button
                type="button"
                className={cn(styles.chip, styles.chipActive)}
                aria-pressed
                title="Показать все заявки"
                onClick={() => setMassIncident('')}
              >
                <MassIncidentIcon size={14} aria-hidden />
                {oneIncidentTitle || 'Один сбой'}
                <X size={14} aria-hidden />
              </button>
            )}
            <button
              type="button"
              className={cn(styles.chip, massIncident === 'only' && styles.chipActive)}
              aria-pressed={massIncident === 'only'}
              onClick={() => setMassIncident((m) => (m === 'only' ? '' : 'only'))}
            >
              <MassIncidentIcon size={14} aria-hidden />
              Только сбои
            </button>
            <button
              type="button"
              className={cn(styles.chip, massIncident === 'hide' && styles.chipActive)}
              aria-pressed={massIncident === 'hide'}
              onClick={() => setMassIncident((m) => (m === 'hide' ? '' : 'hide'))}
            >
              Скрыть сбои
            </button>
          </div>
        </FilterGroup>
      )}

      </div>

      <div className={styles.filtersCol}>
      {isStaff && (
        <FilterGroup title="Приоритет" Icon={TaskPriorityIcon}>
          <Select
            aria-label="Приоритет"
            value={priority}
            onValueChange={setPriority}
            options={[
              { value: '', label: 'Любой приоритет' },
              ...TICKET_PRIORITIES.map((p) => ({
                value: p,
                label: TICKET_PRIORITY_LABELS[p],
              })),
            ]}
          />
        </FilterGroup>
      )}

      {tab === 'group' && myGroups.length > 1 ? (
        <FilterGroup title="Мои группы" Icon={GroupIcon}>
          <Combobox
            multiple
            display="summary"
            aria-label="Мои группы"
            value={groupTabIds}
            onValueChange={(next) =>
              setGroupId(next.length === 0 ? '' : next.length === myGroups.length ? 'my' : next.join(','))
            }
            placeholder="Основная группа"
            emptyText="Группа не найдена"
            options={myGroups.map((g) => ({
              value: g.id,
              label: g.name,
              subtitle: g.isPrimary ? 'основная' : null,
            }))}
          />
        </FilterGroup>
      ) : (
        canSeeGroups &&
        groups.length > 0 &&
        tab !== 'group' && (
          <FilterGroup title="Группа" Icon={GroupIcon}>
            <Combobox
              multiple
              display="summary"
              aria-label="Группа"
              value={groupIds}
              onValueChange={(next) => setGroupId(next.join(','))}
              placeholder="Все группы"
              emptyText="Группа не найдена"
              options={[
                { value: 'none', label: 'Без группы' },
                ...groups.map((g) => ({ value: g.id, label: g.name })),
              ]}
            />
          </FilterGroup>
        )
      )}

      {isStaff && (
        <FilterGroup title="Категория" Icon={CategoryIcon}>
          <Combobox
            multiple
            display="summary"
            aria-label="Категория"
            value={catIds}
            onValueChange={(next) => {
              const keep = new Set(next.filter((c) => c !== 'none'));
              const svcKeep =
                keep.size === 0
                  ? svcIds
                  : svcIds.filter(
                      (id) =>
                        id === 'none' ||
                        categories.some((c) => keep.has(c.id) && c.Subcategories?.some((sc) => sc.id === id)),
                    );
              patchUrlParams({ cat: next.join(','), svc: svcKeep.join(',') });
            }}
            placeholder="Любая категория"
            emptyText="Категория не найдена"
            options={categoryOptions}
          />
        </FilterGroup>
      )}

      {isStaff && (
        <FilterGroup title="Услуга" Icon={ServiceIcon}>
          <Combobox
            multiple
            display="summary"
            aria-label="Услуга"
            value={svcIds}
            onValueChange={(next) => setSvc(next.join(','))}
            placeholder="Любая услуга"
            emptyText="Услуга не найдена"
            options={serviceOptions}
          />
        </FilterGroup>
      )}
      </div>
    </div>
  );

  return (
    <PageMotion className={styles.pageMotion}>
      <main className={styles.page}>
        <section className={styles.list} data-list-column>
          <div className={styles.toolbar}>
            {isStaff && (
              <Scroller
                axis="x"
                className={styles.tabs}
                viewportClassName={styles.tabsRow}
                viewportRef={tabsViewportRef}
                role="tablist"
                aria-label="Представление"
                data-tour="workspace-views"
              >
                {VIEW_TABS.filter((t) => t.value !== 'group' || myGroups.length > 0).map((t) =>
                  t.value === 'group' && tab === 'group' && myGroups.length > 1 ? (
                    <Menu key={t.value}>
                      <MenuTrigger
                        render={
                          <button
                            type="button"
                            role="tab"
                            aria-selected={!anySetApplied}
                            aria-label={`Группы: ${groupTabLabel}. Выбрать группы`}
                            className={cn(styles.tabBtn, anySetApplied ? styles.tabBtnBase : styles.tabBtnActive)}
                          />
                        }
                      >
                        <GroupIcon size={14} aria-hidden />
                        <span className={styles.tabBtnLabel}>{groupTabLabel}</span>
                        <ChevronDown size={14} aria-hidden />
                      </MenuTrigger>
                      <MenuContent align="start" className={styles.groupMenu}>
                        {myGroups.map((g) => {
                          const on = groupTabIds.includes(g.id);
                          return (
                            <MenuItem
                              key={g.id}
                              closeOnClick={false}
                              aria-checked={on}
                              role="menuitemcheckbox"
                              onClick={() => toggleGroupTabGroup(g.id)}
                            >
                              <span className={styles.groupMenuText}>
                                <span className={styles.groupMenuName}>{g.name}</span>
                                {g.isPrimary && <span className={styles.groupMenuHint}>основная</span>}
                              </span>
                              <span className={styles.groupMenuCheck} aria-hidden>
                                {on && <Check size={14} />}
                              </span>
                            </MenuItem>
                          );
                        })}
                        <MenuSeparator />
                        <MenuItem onClick={() => setGroupId('my')}>Все мои группы</MenuItem>
                        {primaryGroupId && (
                          <MenuItem onClick={() => setGroupId('')}>Только основная</MenuItem>
                        )}
                      </MenuContent>
                    </Menu>
                  ) : (
                    <button
                      type="button"
                      key={t.value}
                      role="tab"
                      aria-selected={tab === t.value && !anySetApplied}
                      className={cn(
                        styles.tabBtn,
                        tab === t.value && (anySetApplied ? styles.tabBtnBase : styles.tabBtnActive),
                      )}
                      onClick={() => handleTab(t.value)}
                    >
                      {t.label}
                    </button>
                  ),
                )}
                {isStaff && (
                  <span className={styles.tabSets}>
                    <span className={styles.tabsDivider} aria-hidden />
                    <SetTabs {...setProps} />
                  </span>
                )}
              </Scroller>
            )}

            { }
            <div className={styles.tools}>
              <div className={styles.actions}>
                <ToolbarSearch
                  value={search}
                  onChange={setSearch}
                  open={searchOpen}
                  onOpenChange={setSearchOpen}
                  pinned={searchPinned}
                  label="Поиск по заявкам"
                  placeholder={searchPinned ? 'Тема, номер, автор' : 'Тема, номер или заявитель'}
                  tooltip="Поиск по теме, номеру или заявителю"
                />

                <SortMenu
                  value={sort}
                  onChange={setSort}
                  options={SORT_OPTIONS.filter((o) => isStaff || o.value !== 'priority')}
                />

                {canGroupIncidents && (
                  <IconButton
                    label={selecting ? 'Закончить выбор' : 'Выбрать заявки'}
                    tooltip={selecting ? 'Закончить выбор' : 'Выбрать несколько заявок — например, в массовый сбой'}
                    size="lg"
                    aria-pressed={selecting}
                    className={cn(selecting && styles.actionOn)}
                    onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
                  >
                    <ListChecks size={18} aria-hidden />
                  </IconButton>
                )}

                <div className={styles.filtersAnchor} ref={filtersRef}>
                  <IconButton
                    data-tour="list-filters"
                    label="Фильтры"
                    tooltip="Статус, SLA, группа, категория и услуга"
                    size="lg"
                    aria-expanded={filtersOpen}
                    className={cn(filtersOpen && styles.actionOn)}
                    onClick={() => setFiltersOpen((v) => !v)}
                  >
                    <Filter size={18} aria-hidden />
                    {activeFilterCount > 0 && (
                      <span className={styles.filtersBadge}>{activeFilterCount}</span>
                    )}
                  </IconButton>

                  { }
                  {!isMobile && filtersOpen && (
                    <div ref={popoverRef} className={styles.popover} role="dialog" aria-label="Фильтры">
                      <div className={styles.popoverHead}>
                        <span>Фильтры</span>
                        {hasFilters && (
                          <button type="button" className={styles.popoverReset} onClick={resetFilters}>
                            Сбросить
                          </button>
                        )}
                      </div>
                      <Scroller className={styles.popoverBody}>{filtersBody}</Scroller>
                      <div className={styles.popoverFoot}>
                        <span className={styles.popoverFootHint}>
                          {appliedView ? (
                            <>
                              <BoardViewIcon size={14} aria-hidden />
                              Набор «{appliedView.name}»
                            </>
                          ) : hiddenPresets.length > 0 ? (
                            <button type="button" className={styles.restoreLink} onClick={() => void restorePresets()}>
                              Вернуть встроенные наборы ({hiddenPresets.length})
                            </button>
                          ) : (
                            'Фильтры применяются сразу'
                          )}
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!canSaveSet}
                          onClick={() => void saveView()}
                        >
                          <BoardViewIcon size={14} aria-hidden />
                          Сохранить набор
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {hasFilters && (
                  <IconButton
                    label="Сбросить фильтры"
                    size="lg"
                    tone="danger"
                    className={styles.actionReset}
                    onClick={resetFilters}
                  >
                    <FilterX size={18} aria-hidden />
                  </IconButton>
                )}
              </div>
            </div>
          </div>

          { }
          {isMobile && (
            <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
              <DialogContent
                ref={sheetRef}
                flush
                className={styles.sheet}
                backdropClassName={styles.sheetBackdrop}
              >
                <div className={styles.sheetTop}>
                  { }
                  <div className={styles.sheetGrip} {...gripHandlers} role="presentation">
                    <span className={styles.sheetGripBar} />
                  </div>
                  <div className={styles.sheetHead}>
                    <DialogTitle className={styles.sheetTitle}>Фильтры</DialogTitle>
                    <div className={styles.sheetHeadActions}>
                      {hasFilters && (
                        <button type="button" className={styles.popoverReset} onClick={resetFilters}>
                          Сбросить
                        </button>
                      )}
                      <IconButton label="Закрыть" onClick={() => setFiltersOpen(false)}>
                        <X size={16} aria-hidden />
                      </IconButton>
                    </div>
                  </div>
                </div>
                <div className={styles.sheetBody}>
                  {filtersBody}
                  {hiddenPresets.length > 0 && (
                    <button type="button" className={styles.restoreLink} onClick={() => void restorePresets()}>
                      Вернуть встроенные наборы ({hiddenPresets.length})
                    </button>
                  )}
                </div>
                <div className={styles.sheetFooter}>
                  {canSaveSet && (
                    <Button variant="secondary" size="lg" onClick={() => void saveView()}>
                      <BoardViewIcon size={14} aria-hidden />
                      Сохранить
                    </Button>
                  )}
                  <Button size="lg" fullWidth onClick={() => setFiltersOpen(false)}>
                    Показать заявки
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {isStaff && <MobileSetsRow {...setProps} chips={activeChips} />}
          <ActiveFiltersBar chips={activeChips} />

          <div className={styles.listScroll}>
            {!restoring && (
              <TicketList
                params={params}
                staffView={isStaff}
                onSelect={selecting ? togglePicked : undefined}
                selectedIds={selecting ? picked : undefined}
              />
            )}
          </div>

          {selecting && (
            <div className={styles.selectBar} role="toolbar" aria-label="Выбранные заявки">
              <span className={styles.selectCount}>
                {picked.size === 0
                  ? 'Отметьте заявки в списке'
                  : `Выбрано ${picked.size} ${pluralRu(picked.size, 'заявка', 'заявки', 'заявок')}`}
              </span>
              <div className={styles.selectActions}>
                <Button variant="ghost" onClick={stopSelecting}>
                  Отмена
                </Button>
                <Button
                  disabled={picked.size === 0}
                  onClick={() =>
                    openIncidentPicker({ ticketIds: [...picked], onDone: stopSelecting })
                  }
                >
                  <MassIncidentIcon size={14} aria-hidden />
                  В массовый сбой…
                </Button>
              </div>
            </div>
          )}
        </section>
      </main>
    </PageMotion>
  );
};
